import { useContext, useEffect, type ChangeEvent, type KeyboardEvent } from 'react';
import {
    default as ReactDatePicker,
    ReactDatePickerCustomHeaderProps,
    type ReactDatePickerCustomDayNameProps,
    type DatePickerProps as ReactDatePickerProps,
} from 'react-datepicker';
import Button from '~/components/new/Button';
import { Select } from '~/components/ui/Form';
import type { SelectOption } from '~/components/ui/forms/Select';
import { RemixIconNames } from '~/components/ui/Icon';
import { modalContext } from '~/components/ui/Modal';
import { MONTH_OPTIONS } from '~/utils/format';
import { setRemixiconAsCSSVar } from '~/utils/icons';
import type { DataTestAttributes } from '~/utils/qa';
import { classNames } from '~/utils/styles';
import { z } from '~/utils/zIndex';

export type DatePickerProps = {
    disabled?: boolean;
    value?: string | Date | number;
    showTime?: boolean;
    error?: boolean;
    placeholder?: string;
    showMonthYearPicker?: boolean;
} & Omit<ReactDatePickerProps, 'value'> &
    DataTestAttributes;

export default function DatePicker({
    disabled,
    value,
    showTime = true,
    error,
    placeholder = '',
    showMonthYearPicker = false,
    minDate,
    maxDate,
    ['data-test-component']: testComponent = 'ui/forms/DatePicker',
    ['data-test-name']: testName,
    ['data-test-id']: testId,
    ...props
}: DatePickerProps) {
    let val: string | Date | number | null;

    if (!isConvertibleToDate(value)) {
        val = null;
    } else if ((value as any) instanceof Date) {
        val = value;
    } else if (typeof value === 'string' && value.endsWith('Z')) {
        // Remove the Z at the end of the date time if it's there.
        val = new Date(value.slice(0, -1));
    } else {
        val = new Date(value);
    }

    const dateFormat = showMonthYearPicker
        ? 'MM/yyyy'
        : showTime
          ? 'MM/dd/yyyy HH:mm'
          : 'MM/dd/yyyy';

    useEffect(() => {
        setRemixiconAsCSSVar('--datepicker-calendar-icon', RemixIconNames.RiCalendarLine);
        setRemixiconAsCSSVar('--datepicker-clear', RemixIconNames.RiCloseCircleFill);
    }, []);

    const { invalidateCloseOnClickOutside } = useContext(modalContext);

    return (
        // the wrapper puts field and pop-up calendar into the same component avoiding gap from parent element
        <div
            data-test-component={testComponent}
            data-test-name={testName}
            data-test-id={testId}>
            <ReactDatePicker
                selected={val as Date | null}
                showTimeInput={showTime}
                disabled={disabled}
                timeFormat='HH:mm'
                className={classNames({
                    error: Boolean(error),
                    'form-control': true,
                })}
                showMonthYearPicker={showMonthYearPicker}
                minDate={minDate}
                maxDate={maxDate}
                onKeyDown={(e: KeyboardEvent<HTMLElement>) => e.preventDefault()}
                dateFormat={dateFormat}
                renderCustomDayName={({ shortName }: ReactDatePickerCustomDayNameProps) =>
                    shortName
                }
                renderCustomHeader={(headerProps: ReactDatePickerCustomHeaderProps) => (
                    <DatePickerHeader
                        showMonthYearPicker={showMonthYearPicker}
                        minDate={minDate}
                        maxDate={maxDate}
                        {...headerProps}
                    />
                )}
                customTimeInput={<TimePicker />}
                placeholderText={placeholder}
                {...(props as ReactDatePickerProps)}
                id={props.id || props.name}
                portalId='default-datepicker-portal'
                onCalendarOpen={() => {
                    invalidateCloseOnClickOutside(true);
                }}
                onCalendarClose={() => {
                    invalidateCloseOnClickOutside(false);
                }}
            />
        </div>
    );
}

function pad(n: number) {
    return n < 10 ? `0${n}` : n;
}

function TimePicker({ date, onChange }: { date?: Date; onChange?: (value: string) => void }) {
    const hours = date ? date.getHours() : 0;
    const minutes = date ? date.getMinutes() : 0;

    const updateHours = (e: ChangeEvent<HTMLInputElement>) => {
        let num = Number(e.target.value);
        if (isNaN(num) || num > 23 || num < 0) {
            num = 0;
        }
        onChange?.(`${pad(num)}:${pad(date?.getMinutes() ?? 0)}`);
    };

    const updateMinutes = (e: ChangeEvent<HTMLInputElement>) => {
        let num = Number(e.target.value);
        if (isNaN(num) || num > 59 || num < 0) {
            num = 0;
        }
        onChange?.(`${pad(date?.getHours() ?? 0)}:${pad(num)}`);
    };

    return (
        <div className='react-datepicker__time-picker'>
            <input
                type='text'
                className='react-datepicker__time-picker__input form-control'
                value={`${pad(hours)}`}
                onChange={updateHours}
            />
            <div className='react-datepicker__time-picker__separator'>:</div>
            <input
                type='text'
                className='react-datepicker__time-picker__input form-control'
                value={`${pad(minutes)}`}
                onChange={updateMinutes}
            />
        </div>
    );
}

export type DatePickerHeaderProps = {
    showMonthYearPicker?: boolean;
    minDate?: Date | null;
    maxDate?: Date | null;
} & ReactDatePickerCustomHeaderProps;

function buildYearOptions(
    date: Date,
    minDate?: Date | null,
    maxDate?: Date | null,
): SelectOption<number>[] {
    const currentYear = new Date().getFullYear();
    const selectedYear = date.getFullYear();
    const maxYear = maxDate ? maxDate.getFullYear() : Math.max(selectedYear, currentYear + 1);
    const minYear = minDate ? minDate.getFullYear() : 1950;

    const yearOptions: SelectOption<number>[] = [];
    for (let i = maxYear; i >= minYear; i--) {
        yearOptions.push({ label: `${i}`, value: i, 'data-test-name': 'year' });
    }
    return yearOptions;
}

function DatePickerHeader({
    date,
    changeYear,
    decreaseYear,
    increaseYear,
    prevYearButtonDisabled,
    nextYearButtonDisabled,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
    showMonthYearPicker = false,
    minDate,
    maxDate,
}: DatePickerHeaderProps) {
    const yearOptions = buildYearOptions(date, minDate, maxDate);

    if (showMonthYearPicker) {
        return (
            <div className='react-datepicker__nav'>
                <Button
                    variant='floating'
                    size='medium'
                    icon={RemixIconNames.RiArrowLeftSLine}
                    title='Previous year'
                    onClick={decreaseYear}
                    disabled={prevYearButtonDisabled}
                    data-test-name='previousYear'
                />

                <div className='react-datepicker__nav__year'>
                    <Select
                        options={yearOptions}
                        value={date.getFullYear()}
                        onChange={(v) => changeYear(v?.value)}
                        data-test-name='year'
                    />
                </div>

                <Button
                    variant='floating'
                    size='medium'
                    icon={RemixIconNames.RiArrowRightSLine}
                    title='Next year'
                    onClick={increaseYear}
                    disabled={nextYearButtonDisabled}
                    data-test-name='nextYear'
                />
            </div>
        );
    }

    return (
        <div className='react-datepicker__nav'>
            <Button
                variant='floating'
                size='medium'
                icon={RemixIconNames.RiArrowLeftSLine}
                title='Previous month'
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
                data-test-name='previousMonth'
            />

            <div
                className='react-datepicker__nav__month'
                data-testid='month-selector'>
                <Select
                    options={MONTH_OPTIONS}
                    value={date.getMonth() + 1}
                    onChange={(v) => changeMonth(v?.value - 1)}
                    data-test-name='month'
                />
            </div>
            <div
                className='react-datepicker__nav__year'
                data-testid='year-selector'>
                <Select
                    options={yearOptions}
                    value={date.getFullYear()}
                    onChange={(v) => changeYear(v?.value)}
                    data-test-name='year'
                />
            </div>

            <Button
                variant='floating'
                size='medium'
                icon={RemixIconNames.RiArrowRightSLine}
                title='Next month'
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
                data-test-name='nextMonth'
            />
        </div>
    );
}

function isConvertibleToDate(input?: string | Date | number | boolean | symbol): input is string {
    return (
        !!input &&
        typeof input !== 'boolean' &&
        typeof input !== 'symbol' &&
        !isNaN(new Date(input).getTime())
    );
}

export function DatepickerPortal() {
    return (
        <div
            id='default-datepicker-portal'
            className='react-datepicker__tab-loop'
            style={{
                position: 'absolute',
                zIndex: z.dropdown,
                minWidth: '350px',
            }}
        />
    );
}
